import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Routes, Route } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { Suspense, lazy } from 'react';
import theme from './theme';
import Box from '@mui/material/Box';
import { BackgroundBeams } from './components/ui/background-beams';

// Layout Components
import Header from './components/layout/Header';

// Lazy load all major components for code splitting
const Hero = lazy(() => import('./components/homepage/Hero'));
const NewFeatures = lazy(() => import('./components/homepage/NewFeatures'));
const Footer = lazy(() => import('./components/homepage/Footer'));

const AuthFlow = lazy(() => import('./components/auth/AuthFlow'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const PublicSocialCard = lazy(() => import('./pages/PublicSocialCard'));
const SocialCard = lazy(() => import('./pages/SocialCard'));

// Dashboard Pages
const DashboardSocialCard = lazy(() => import('./pages/dashboard/SocialCard'));
const DashboardAchievements = lazy(() => import('./pages/dashboard/Achievements'));
const DashboardGames = lazy(() => import('./pages/dashboard/Games'));
const DashboardFriends = lazy(() => import('./pages/dashboard/Friends'));
const DashboardNotifications = lazy(() => import('./pages/dashboard/Notifications'));
const DashboardFeed = lazy(() => import('./pages/dashboard/Feed'));
const DashboardLayout = lazy(() => import('./components/dashboard/DashboardLayout'));

// Components
const AuthGuard = lazy(() => import('./components/common/AuthGuard'));

// Loading component for suspense fallback
const LoadingFallback = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    background: 'linear-gradient(135deg, #1E1B4B 0%, #0A0A0F 100%)'
  }}>
    <div style={{
      width: '40px',
      height: '40px',
      border: '4px solid rgba(255, 255, 255, 0.2)',
      borderTop: '4px solid #6B46C1',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite'
    }} />
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

// Homepage Component with lazy loaded components
function Homepage() {
  return (
    <Box sx={{ position: 'relative', minHeight: '100vh', background: '#000' }}>
      {/* Fixed Background Beams */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 0,
          background: 'linear-gradient(135deg, #0a0a0a 0%, #000000 100%)',
        }}
      >
        <BackgroundBeams />
      </Box>
      
      {/* Scrollable Content */}
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Suspense fallback={<LoadingFallback />}>
          <Header />
          <Hero />
          <NewFeatures />
          <Footer />
        </Suspense>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AnimatePresence mode="wait">
        <Routes>
          {/* Home/Landing Route */}
          <Route path="/" element={<Homepage />} />

          {/* Auth Route */}
          <Route path="/auth" element={
            <Suspense fallback={<LoadingFallback />}>
              <AuthFlow initialMode="connect" />
            </Suspense>
          } />

          {/* Public routes */}
          <Route path="/social-card" element={
            <Suspense fallback={<LoadingFallback />}>
              <SocialCard />
            </Suspense>
          } />
          
          {/* Protected dashboard routes */}
          <Route path="/dashboard" element={
            <Suspense fallback={<LoadingFallback />}>
              <AuthGuard>
                <DashboardLayout />
              </AuthGuard>
            </Suspense>
          }>
            <Route index element={
              <Suspense fallback={<LoadingFallback />}>
                <Dashboard />
              </Suspense>
            } />
            <Route path="social-card" element={
              <Suspense fallback={<LoadingFallback />}>
                <DashboardSocialCard />
              </Suspense>
            } />
            <Route path="achievements" element={
              <Suspense fallback={<LoadingFallback />}>
                <DashboardAchievements />
              </Suspense>
            } />
            <Route path="games" element={
              <Suspense fallback={<LoadingFallback />}>
                <DashboardGames />
              </Suspense>
            } />
            <Route path="friends" element={
              <Suspense fallback={<LoadingFallback />}>
                <DashboardFriends />
              </Suspense>
            } />
            <Route path="notifications" element={
              <Suspense fallback={<LoadingFallback />}>
                <DashboardNotifications />
              </Suspense>
            } />
            <Route path="feed" element={
              <Suspense fallback={<LoadingFallback />}>
                <DashboardFeed />
              </Suspense>
            } />
          </Route>
          
          {/* Catch-all for @ handles - MUST BE LAST */}
          <Route path="/*" element={
            <Suspense fallback={<LoadingFallback />}>
              <PublicSocialCard />
            </Suspense>
          } />
        </Routes>
      </AnimatePresence>
    </ThemeProvider>
  );
}

export default App;
